import axios, { AxiosError, AxiosInstance } from 'axios';
import { toast } from '@shadcn/hooks/use-toast';
import { attachIntakeIntegrationRequestInterceptor } from '@forms/form-render/http/intakeIntegrationRouting';
import { getEnquiryCookie } from './enquiries/cookie';

// Helper to wait for a specified time
const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Create axios instance
const apiClient: AxiosInstance = axios.create();
attachIntakeIntegrationRequestInterceptor(apiClient);

// Configuration
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000; // 1 second

apiClient.interceptors.request.use((config) => {
    const cookie = getEnquiryCookie();
    if (cookie?.enquiryId) {
        config.headers ??= {} as typeof config.headers;
        config.headers['X-Enquiry-Id'] = cookie.enquiryId;
    }
    return config;
});

// Response interceptor to handle rate limiting
apiClient.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const config = error.config;
        
        // Check if this is a 429 error
        if (error.response?.status === 429) {
            const retryCount = (config as any).__retryCount || 0;

            const delayMs = INITIAL_RETRY_DELAY * Math.pow(2, retryCount);
            
            // Only retry if we haven't exceeded max retries
            if (retryCount < MAX_RETRIES) {
                (config as any).__retryCount = retryCount + 1;
                
                console.warn(`Rate limit hit. Retrying in ${delayMs / 1000}s... (attempt ${retryCount + 1}/${MAX_RETRIES})`);
                
                await wait(delayMs);
                return apiClient.request(config);
            }
            
            toast({
                variant: "destructive",
                title: "Too Many Requests",
                description: "You're submitting too quickly. Please wait a moment before trying again."
            });
            
            const userError = new Error(
                'Too many requests. Please wait a moment and try again.'
            );
            (userError as any).isRateLimitError = true;
            (userError as any).originalError = error;
            throw userError;
        }
        
        // For other errors, pass through
        return Promise.reject(error);
    }
);

export { apiClient };
export default apiClient;

